"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "cn";
import { Check, Copy } from "lucide-react";
import * as React from "react";
type CodeBlockProps = React.ComponentProps<"figure"> & {
  /** The code, line breaks included. */
  code: string;
  /** A short tag over the code, such as "Input". */
  label?: string;
  /** What the code is, such as an operation's name. */
  title?: string;
  /** Colors keys, strings, numbers, and literals when the code is JSON. */
  language?: "json";
  /** Whether a button copies the code to the clipboard. */
  copyable?: boolean;
};
const JSON_TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b/g;

/** The JSON with its keys, strings, numbers, and literals in their own colors. */
function highlightJson(code: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  for (const match of code.matchAll(JSON_TOKEN)) {
    const [text, string, colon, number, literal] = match;
    const at = match.index;
    if (at > last) nodes.push(code.slice(last, at));
    if (string !== undefined) {
      nodes.push(<span key={at} className={colon ? "text-deepjade-300" : "text-marble-200"}>
          {string}
        </span>);
      if (colon) nodes.push(colon);
    } else if (number !== undefined || literal !== undefined) {
      nodes.push(<span key={at} className="text-jade-300">
          {text}
        </span>);
    }
    last = at + text.length;
  }
  if (last < code.length) nodes.push(code.slice(last));
  return nodes;
}
function CopyButton(t0) {
  const $ = _c(12);
  const {
    code
  } = t0;
  const [copied, setCopied] = React.useState(false);
  let t1;
  let t2;
  if ($[0] !== copied) {
    t1 = () => {
      if (!copied) {
        return;
      }
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    };
    t2 = [copied];
    $[0] = copied;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[3] !== code) {
    t3 = async () => {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    };
    $[3] = code;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("inline-flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-sm border border-marble-900 px-2", "label-sm text-marble-500 outline-none transition-colors", "hover:text-marble-100 focus-visible:shadow-[0_0_0_3px_var(--ring)]");
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== copied) {
    t5 = copied ? <Check aria-hidden="true" className="size-3" /> : <Copy aria-hidden="true" className="size-3" />;
    $[6] = copied;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = copied ? "Copied" : "Copy";
  let t7;
  if ($[8] !== t3 || $[9] !== t5 || $[10] !== t6) {
    t7 = <button type="button" data-slot="code-block-copy" onClick={t3} className={t4}>{t5}{t6}</button>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}

/**
 * A block of code on a dark surface, the same in both themes, with an
 * optional header naming what it is and a button to copy it.
 */
function CodeBlock(t0) {
  const $ = _c(25);
  let className;
  let code;
  let label;
  let language;
  let props;
  let t1;
  let title;
  if ($[0] !== t0) {
    ({
      code,
      label,
      title,
      language,
      copyable: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = code;
    $[3] = label;
    $[4] = language;
    $[5] = props;
    $[6] = t1;
    $[7] = title;
  } else {
    className = $[1];
    code = $[2];
    label = $[3];
    language = $[4];
    props = $[5];
    t1 = $[6];
    title = $[7];
  }
  const copyable = t1 === undefined ? true : t1;
  let t2;
  if ($[8] !== className) {
    t2 = cn("flex min-w-0 flex-col overflow-hidden rounded-md bg-marble-950 text-marble-100", className);
    $[8] = className;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== code || $[11] !== copyable || $[12] !== label || $[13] !== title) {
    t3 = (label || title || copyable) && <figcaption data-slot="code-block-header" className="flex items-center justify-between gap-3 border-marble-900 border-b px-4 py-2"><div className="flex min-w-0 items-center gap-2.5">{label && <span className="caption-sm uppercase tracking-wider text-jade-300">{label}</span>}{title && <span className="mono truncate text-marble-500 normal-case">{title}</span>}</div>{copyable && <CopyButton code={code} />}</figcaption>;
    $[10] = code;
    $[11] = copyable;
    $[12] = label;
    $[13] = title;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== code || $[16] !== language) {
    t4 = language === "json" ? highlightJson(code) : code;
    $[15] = code;
    $[16] = language;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t4) {
    t5 = <pre data-slot="code-block-code" className="mono m-0 overflow-auto px-4 py-3 normal-case"><code>{t4}</code></pre>;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== props || $[21] !== t2 || $[22] !== t3 || $[23] !== t5) {
    t6 = <figure data-slot="code-block" className={t2} {...props}>{t3}{t5}</figure>;
    $[20] = props;
    $[21] = t2;
    $[22] = t3;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
}
export { CodeBlock, type CodeBlockProps };